import { useEffect } from 'react';

type PageMeta = {
  title: string;
  description: string;
  /** Absolute URL of this page (canonical + og:url). */
  url: string;
};

/**
 * Per-page <title>, description, canonical and Open Graph tags.
 *
 * public/index.html ships site-wide defaults for all of these; this updates
 * those same tags in place (never adds duplicates) and puts the previous
 * values back when the page unmounts, so pages without their own meta keep
 * the homepage defaults.
 */
const usePageMeta = ({ title, description, url }: PageMeta) => {
  useEffect(() => {
    const targets: Array<[string, string, string]> = [
      ['meta[name="description"]', 'content', description],
      ['meta[property="og:title"]', 'content', title],
      ['meta[property="og:description"]', 'content', description],
      ['meta[property="og:url"]', 'content', url],
      ['meta[name="twitter:title"]', 'content', title],
      ['meta[name="twitter:description"]', 'content', description],
      ['link[rel="canonical"]', 'href', url],
    ];

    const previousTitle = document.title;
    document.title = title;

    const restore = targets.flatMap(([selector, attr, value]) => {
      const el = document.head.querySelector(selector);
      if (!el) return [];
      const previous = el.getAttribute(attr);
      el.setAttribute(attr, value);
      return [() => previous !== null && el.setAttribute(attr, previous)];
    });

    return () => {
      document.title = previousTitle;
      restore.forEach((fn) => fn());
    };
  }, [title, description, url]);
};

export default usePageMeta;
